// missing-chunks — R-18: chunks that failed all attempts (`chunksFailed`,
// api 0.16.0) and how they interleave with the segments that exist.
//
// A job may be `complete` with a few missing chunks, or `failed`
// (chunks_failed) with the good chunks' segments kept. In both cases the
// transcript is shown with a highlighted gap row where each missing
// chunk belongs, and POST /:id/retry re-transcribes only those chunks.
//
// Pure module — unit-tested in node.

import type { TranslationFailedChunk, TranslationSegment } from '@nayto/types';

export type TranscriptRow =
  | { kind: 'segment'; seg: TranslationSegment; index: number }
  | { kind: 'gap'; chunk: TranslationFailedChunk };

/** Sorted copy (by fromSeconds); tolerates a missing / malformed field. */
export function missingChunks(job: {
  chunksFailed?: TranslationFailedChunk[] | null;
}): TranslationFailedChunk[] {
  const list = Array.isArray(job.chunksFailed) ? job.chunksFailed : [];
  return list
    .filter((c) => c != null && Number.isFinite(c.fromSeconds) && Number.isFinite(c.toSeconds))
    .slice()
    .sort((a, b) => a.fromSeconds - b.fromSeconds);
}

/** Segments in their own order, with one gap row inserted before the
 * first segment that starts at or after each missing chunk's start
 * (after every segment when the gap is at the end). */
export function mergeSegmentsWithGaps(
  segments: TranslationSegment[],
  chunks: TranslationFailedChunk[],
): TranscriptRow[] {
  const gaps = missingChunks({ chunksFailed: chunks });
  const rows: TranscriptRow[] = [];
  let g = 0;
  segments.forEach((seg, index) => {
    while (g < gaps.length && gaps[g].fromSeconds <= seg.start) {
      rows.push({ kind: 'gap', chunk: gaps[g] });
      g += 1;
    }
    rows.push({ kind: 'segment', seg, index });
  });
  for (; g < gaps.length; g += 1) rows.push({ kind: 'gap', chunk: gaps[g] });
  return rows;
}

/** 1800 → "30:00", 3725 → "1:02:05" (same clock as formatEta). */
export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(sec).padStart(2, '0')}`;
}

/** "30:00–40:00" */
export function formatChunkRange(
  chunk: Pick<TranslationFailedChunk, 'fromSeconds' | 'toSeconds'>,
): string {
  return `${formatClock(chunk.fromSeconds)}–${formatClock(chunk.toSeconds)}`;
}

/** One line per missing chunk for the "Technical details" block:
 * `chunk 3 (30:00–40:00): <errorDetail>`; '' when there are none. */
export function formatChunkDetails(chunks: TranslationFailedChunk[]): string {
  return missingChunks({ chunksFailed: chunks })
    .map((c) => {
      const detail = c.errorDetail ? `: ${c.errorDetail}` : '';
      return `chunk ${c.index} (${formatChunkRange(c)})${detail}`;
    })
    .join('\n');
}
